import { type FC, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AdsContext } from '@components/Ads/context';

import {
  useBoostPlantAdvertisingMutation,
  useBoostPlantMutation,
} from '@/api/queries/boost';
import { useAdsPlacement } from '@/common/hooks/ads-placement';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { playSound } from '@/common/lib/soundEffect';
import { timeStringFormat } from '@/common/lib/timer';
import { type PlantType } from '@/common/types/common';
import { type BoostRequestAdvertisingType } from '@/common/types/query/boost';
import { StringSmall } from '@/components/Typography/Typography';
import { MainSmButton, WatchButton } from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { SecondaryTimer } from '@/components/ui/Timer/SecondaryTimer';
import { BoostFirebaseEventsContext } from '@/context/BoostFirebaseEventContext';

interface AdsBoostButtonProps {
  plantType: PlantType;
  plantId?: string;
  effectMultiplier: number;
  duration?: number;
  nextUseInSeconds?: number;
}

const AdsBoostButton: FC<AdsBoostButtonProps> = ({
  effectMultiplier,
  duration,
  nextUseInSeconds,
  plantId,
  plantType,
}) => {
  const { t } = useTranslation();
  const statusVIP = useCheckVIPstatus();
  const { showAd, adsIsLoading } = useContext(AdsContext);
  const { onBoostSuccessEvent } = useContext(BoostFirebaseEventsContext);
  const [, boostPlantRequestState] = useBoostPlantMutation({
    fixedCacheKey: 'shared-boost-mutation-request',
  });

  const [boostPlant, { isLoading }] = useBoostPlantAdvertisingMutation({
    fixedCacheKey: 'shared-ads-boost-mutation-request',
  });
  const placements = useAdsPlacement();

  const onReward = useCallback(() => {
    const body: BoostRequestAdvertisingType = {
      plantType,
      plantId,
      activationType: 'Advertising',
    };
    boostPlant(body)
      .unwrap()
      .then(() => {
        onBoostSuccessEvent('boost_ads_activated', {
          duration: duration as number,
          multiplier: effectMultiplier,
        });
      })
      .catch(() => {
        showToast(t('Ads launch error'));
      });
  }, [plantId, onBoostSuccessEvent, duration, effectMultiplier, boostPlant, t]);

  const onAdsBoost = (): void => {
    playSound('buttons/boost');

    if (statusVIP) {
      onReward();
    } else {
      showAd({
        placements,
        onReward,
        onError: () => {
          showToast(t('Ads launch error'));
        },
      });
    }
  };

  return nextUseInSeconds ? (
    <MainSmButton
      className="!bg-transparent !h-8"
      text={''}
      inactive={true}
      disabled={true}
    >
      <StringSmall className="flex gap-[5px] !text-[14px] items-center justify-center font-[500] !text-white/40">
        {t('Next in')}
        <SecondaryTimer
          className="bg-transparent p-0"
          value={timeStringFormat(nextUseInSeconds * 1000)}
          variant="boostX5X10"
        />
      </StringSmall>
    </MainSmButton>
  ) : (
    <WatchButton
      isLoading={adsIsLoading || isLoading}
      disabled={isLoading || boostPlantRequestState.isLoading || adsIsLoading}
      onClick={onAdsBoost}
      text="ADS"
      className="!h-8"
      isVIP={statusVIP}
    />
  );
};

export { AdsBoostButton };
